/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { CompetenceActivityClass, EvidenceDefinition, EvidencePart, IEvidence, ProgramData, ProgressCheck } from "@interfaces/Interfaces";
import { IonItem, IonLabel, IonList, IonSelect, IonSelectOption } from "@ionic/react";
import { Text, View } from "@react-pdf/renderer";
import { Capacitor } from "@capacitor/core";
import { updatePopoverTriggerWidth } from "@utils/popoverUtils";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import { useRecoilValueLoadable } from "recoil";
import { evidenceAtom } from "@state/State";
import { format, isValid } from "date-fns";
import { useHistory, useLocation } from "react-router";

type SelectOption = {
  key: string;
  label: string;
  filterType: "competence" | "definition";
};

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
  program?: ProgramData;
};

const deferHeavyWorkAfterModalMs = (): number => {
  switch (Capacitor.getPlatform()) {
    case "ios":
      return 550;
    case "android":
      return 320;
    default:
      return 0;
  }
};

const formatEvidenceRowDate = (dateValue: unknown): string => {
  const d = new Date(dateValue as string | number | Date);
  if (!isValid(d)) return "—";
  return format(d, "dd / MM / yyyy");
};

const DEFINITION_SELECTION_TYPES: EvidenceDefinition["EvidenceSelectionType"][] = [
  "skills",
  "capabilities",
  "medical-imaging",
];

const DEFINITION_SELECTION_TYPE_LABELS: Partial<Record<NonNullable<EvidenceDefinition["EvidenceSelectionType"]>, string>> = {
  skills: "Clinical Skills Log",
  capabilities: "Four Pillars",
};

const baseEvidenceFilter = (item: IEvidence): boolean => {
  const evidenceJSON = item.evidenceJSON;
  return Boolean(evidenceJSON) && !item.draft && (evidenceJSON?.OnHoldReset ?? 0) !== 1;
};

const toSelectOption = (item: IEvidence): SelectOption => {
  const title = item.evidenceJSON?.Title ?? "";
  return { key: String(item.id), label: `${title} - ${formatEvidenceRowDate(item.date)}`, filterType: "competence" };
};

const getEvidenceForCompetence = (evidence: IEvidence[] | null | undefined, competenceId: string): SelectOption[] => {
  const list = Array.isArray(evidence) ? evidence : [];
  return list
    .filter((item) => {
      if (!baseEvidenceFilter(item)) return false;
      const competenceID = item.evidenceJSON?.programInfo?.competenceID ?? "";
      const progressCheckCompetenceID = item.evidenceJSON?.programInfo?.progressCheckCompetenceID ?? "";
      return competenceID === competenceId || progressCheckCompetenceID === competenceId;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map(toSelectOption);
};

const getEvidenceForDefinition = (evidence: IEvidence[] | null | undefined, definitionId: string): SelectOption[] => {
  const list = Array.isArray(evidence) ? evidence : [];
  return list
    .filter((item) => {
      if (!baseEvidenceFilter(item)) return false;
      return item.evidenceJSON?.programInfo?.programEvidenceDefinitionID === definitionId;
    })
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map((item) => ({ ...toSelectOption(item), filterType: "definition" as const }));
};

const getEvidenceForOption = (evidence: IEvidence[] | null | undefined, option: SelectOption): SelectOption[] => {
  if (option.filterType === "definition") {
    return getEvidenceForDefinition(evidence, option.key);
  }
  return getEvidenceForCompetence(evidence, option.key);
};

const PDFDisplay: React.FC<Props> = (props) => {
  const displayLabel = props.data?.label ?? props.data ?? "";
  return (
    <View>
      <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
      <Text style={props.pdfStyles.evidenceDate}>{displayLabel}</Text>
    </View>
  );
};

const DisplayMode: React.FC<Props> = (props) => {
  const history = useHistory();
  const location = useLocation();
  const allEvidenceLB = useRecoilValueLoadable(evidenceAtom);
  const displayLabel = props.data?.label ?? props.data ?? "";
  const evidenceId = props.data?.id;

  const evidenceExists =
    evidenceId &&
    allEvidenceLB.state === "hasValue" &&
    (allEvidenceLB.contents ?? []).some((e: IEvidence) => String(e.id) === String(evidenceId));

  const handleClick = (): void => {
    let route: string;
    if (location.pathname.startsWith("/dashboard/program")) {
      const programId = props.program?.ID ?? location.pathname.split("/").filter(Boolean)[2];
      route = `/dashboard/program/${programId}/read/${evidenceId}`;
    } else {
      route = `/evidence/read/${evidenceId}`;
    }
    history.push(route);
  };

  return (
    <div className="readEvidenceBlock">
      <div className="readEvidenceHeader">{props.definition["Display Title"] || props.definition.Name}</div>
      {evidenceExists ? (
        <button
          className="acc-underline cursor-pointer text-left text-cta-blue underline"
          onClick={handleClick}
        >
          {displayLabel}
        </button>
      ) : (
        <div className="readEvidenceText">{displayLabel}</div>
      )}
      <div className="mt-4 h-[1px] w-full bg-grey-30" />
    </div>
  );
};

const IonSelectWrapper: React.FC<{
  value: string;
  placeholder: string;
  label: string;
  options: SelectOption[];
  useActionSheet: boolean;
  onChange: (value: string) => void;
}> = ({ value, placeholder, label, options, useActionSheet, onChange }) => (
  <IonList
    mode="md"
    className="reflectionActivityList w-full"
    color="white"
  >
    <IonItem
      mode="md"
      className="reflectionActivityItem"
      color="white"
    >
      <IonSelect
        color="white"
        mode="md"
        className="reflectionActivitySelectItem w-full"
        interface={useActionSheet ? "action-sheet" : "popover"}
        justify="start"
        {...(!useActionSheet
          ? {
              interfaceOptions: {
                size: "cover",
                alignment: "start",
                cssClass: "evidence-select-popover",
              },
            }
          : {})}
        placeholder={placeholder}
        value={value || undefined}
        onIonChange={(ev) => onChange(ev.detail.value)}
        onClick={useActionSheet ? undefined : updatePopoverTriggerWidth}
        aria-label={label}
      >
        {options.map((item) => (
          <IonSelectOption
            color="white"
            key={item.key}
            value={item.key}
          >
            <IonLabel aria-label={item.label}>{item.label}</IonLabel>
          </IonSelectOption>
        ))}
      </IonSelect>
    </IonItem>
  </IonList>
);

const NativeSelectWrapper: React.FC<{
  value: string;
  placeholder: string;
  label: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}> = ({ value, placeholder, label, options, onChange }) => (
  <select
    className="custom-select-arrow-large evidenceSelectText w-full cursor-pointer appearance-none truncate bg-white py-2 pl-0 pr-10 font-acc-600 leading-tight text-grey-80"
    style={{ textDecoration: "var(--accessibility-underline-links)" }}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    aria-label={label}
  >
    <option
      value=""
      disabled
    >
      {placeholder}
    </option>
    {options.map((item) => (
      <option
        key={item.key}
        value={item.key}
      >
        {item.label}
      </option>
    ))}
  </select>
);

const Interactive: React.FC<Props> = (props) => {
  const allEvidenceLB = useRecoilValueLoadable(evidenceAtom);
  const deferOptionsBuild = Capacitor.getPlatform() !== "web";
  const useNativeHtmlSelect = deferOptionsBuild;
  const useActionSheet = Capacitor.getPlatform() === "ios";

  const allEvidence = allEvidenceLB.state === "hasValue" ? (allEvidenceLB.contents ?? []) : [];

  const firstSelectOptions: SelectOption[] = useMemo(() => {
    const seen = new Set<string>();
    const all: SelectOption[] = [];

    const addCompetence = (c: CompetenceActivityClass) => {
      if (!seen.has(c.ID)) {
        seen.add(c.ID);
        all.push({ key: c.ID, label: c.Name, filterType: "competence" });
      }
    };

    const addDefinition = (d: EvidenceDefinition) => {
      if (!seen.has(d.ID) && DEFINITION_SELECTION_TYPES.includes(d.EvidenceSelectionType)) {
        seen.add(d.ID);
        const label = (d.EvidenceSelectionType && DEFINITION_SELECTION_TYPE_LABELS[d.EvidenceSelectionType]) ?? d.Name;
        all.push({ key: d.ID, label, filterType: "definition" });
      }
    };

    (props.program?.CompetenceActivity ?? []).forEach(addCompetence);
    (props.program?.ProgressChecks ?? []).forEach((pc: ProgressCheck) =>
      (pc["Competence/Activity"] ?? []).forEach(addCompetence)
    );
    (props.program?.["Evidence Definitions"] ?? []).forEach(addDefinition);

    return all
      .filter((option) => getEvidenceForOption(allEvidence, option).length > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [props.program, allEvidence]);

  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [selectedEvidence, setSelectedEvidence] = useState<{ id: string; label: string } | null>(
    props.initialValue ?? null
  );

  const [nativeEvidenceOptions, setNativeEvidenceOptions] = useState<SelectOption[]>([]);
  const [nativeOptionsReady, setNativeOptionsReady] = useState(!deferOptionsBuild);

  const memoEvidenceOptions = useMemo((): SelectOption[] => {
    if (deferOptionsBuild || !selectedOption) return [];
    return getEvidenceForOption(allEvidence, selectedOption);
  }, [deferOptionsBuild, allEvidence, selectedOption]);

  useEffect(() => {
    if (!deferOptionsBuild) return;
    if (!selectedOption) {
      setNativeEvidenceOptions([]);
      setNativeOptionsReady(true);
      return;
    }

    let cancelled = false;
    setNativeOptionsReady(false);

    const run = (): void => {
      if (cancelled) return;
      try {
        const ev = allEvidenceLB.state === "hasValue" ? allEvidenceLB.contents ?? [] : [];
        setNativeEvidenceOptions(getEvidenceForOption(ev, selectedOption));
      } catch {
        setNativeEvidenceOptions([]);
      }
      if (!cancelled) setNativeOptionsReady(true);
    };

    const timeoutId = window.setTimeout(run, deferHeavyWorkAfterModalMs());
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [deferOptionsBuild, allEvidenceLB.state, allEvidenceLB.contents, selectedOption]);

  const evidenceOptions = deferOptionsBuild ? nativeEvidenceOptions : memoEvidenceOptions;

  const onFirstSelectChange = (key: string): void => {
    const option = firstSelectOptions.find((o) => o.key === key) ?? null;
    setSelectedOption(option);
    setSelectedEvidence(null);
    props.onValueChanged && props.onValueChanged(null);
  };

  const onEvidenceChange = (key: string): void => {
    const found = evidenceOptions.find((o) => o.key === key);
    if (!found) return;
    const value = { id: found.key, label: found.label };
    setSelectedEvidence(value);
    props.onValueChanged && props.onValueChanged(value);
  };

  if (firstSelectOptions.length === 0) {
    return (
      <div className="reflectionActivityTypeContainer">
        <div className="reflectionTagsInput text-grey-60">{"No evidence to select"}</div>
      </div>
    );
  }

  const evidenceLoadingOrEmpty = deferOptionsBuild && selectedOption && !nativeOptionsReady;

  return (
    <div className="flex flex-col gap-3">
      {/* Type select */}
      <div className="reflectionActivityTypeContainer">
        {useNativeHtmlSelect ? (
          <NativeSelectWrapper
            value={selectedOption?.key ?? ""}
            placeholder="Select type"
            label="Select type"
            options={firstSelectOptions}
            onChange={onFirstSelectChange}
          />
        ) : (
          <IonSelectWrapper
            value={selectedOption?.key ?? ""}
            placeholder="Select type"
            label="Select type"
            options={firstSelectOptions}
            useActionSheet={useActionSheet}
            onChange={onFirstSelectChange}
          />
        )}
      </div>

      {/* Evidence select — only shown once a type is chosen */}
      {selectedOption && (
        <div className="reflectionActivityTypeContainer">
          {evidenceLoadingOrEmpty ? (
            <div
              className="min-h-[48px] rounded-md border border-grey-40 bg-white"
              aria-busy="true"
            />
          ) : evidenceOptions.length === 0 ? (
            <div className="reflectionTagsInput !ml-0 !text-grey-60">{"No evidence of this type"}</div>
          ) : useNativeHtmlSelect ? (
            <NativeSelectWrapper
              value={selectedEvidence?.id ?? ""}
              placeholder="Select evidence"
              label="Select evidence"
              options={evidenceOptions}
              onChange={onEvidenceChange}
            />
          ) : (
            <IonSelectWrapper
              value={selectedEvidence?.id ?? ""}
              placeholder="Select evidence"
              label="Select evidence"
              options={evidenceOptions}
              useActionSheet={useActionSheet}
              onChange={onEvidenceChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

const ProgramEvidenceSelect: React.FC<Props> = (props) => {
  if (props.isPDF) {
    return <PDFDisplay {...props} />;
  }
  if (props.isDisplay) {
    return <DisplayMode {...props} />;
  }
  return <Interactive {...props} />;
};

export default ProgramEvidenceSelect;
